import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Activity, AlertTriangle, Filter, MonitorSmartphone, RefreshCw, Search, ShieldCheck, XCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const TABS = [
  { id: 'audit', label: 'Who Edited', icon: Activity },
  { id: 'logins', label: 'Login History', icon: ShieldCheck },
  { id: 'failed', label: 'Failed Login', icon: AlertTriangle },
  { id: 'errors', label: 'System Errors', icon: AlertTriangle },
  { id: 'devices', label: 'IP / Device Tracker', icon: MonitorSmartphone },
];

const todayStart = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date.toISOString().slice(0, 10);
};

const formatDate = (value) => value ? new Date(value).toLocaleString() : '-';
const getActor = (row) => row.actorName || row.actorEmail || row.contact || row.userId || 'System';
const FIELD_LABELS = {
  name: 'name',
  type: 'type',
  category: 'category',
  fee: 'fee',
  seats: 'seats',
  totalSeats: 'total seats',
  seatsAvailable: 'available seats',
  minimumQualification: 'minimum qualification',
  programmeId: 'programme',
  institutionId: 'institution',
  active: 'status',
  splitPaymentsEnabled: 'split payments',
  email: 'email',
  mobile: 'mobile',
  isActive: 'status',
};
const getSubmittedFields = (body = {}) => Object.keys(body || {})
  .filter((key) => FIELD_LABELS[key] && body[key] !== undefined && body[key] !== null && body[key] !== '')
  .map((key) => FIELD_LABELS[key]);
const isGenericUpdateSummary = (summary) => /^(updated|record updated|course updated|programme updated|institution updated)$/i.test(String(summary || '').trim());
const getTargetLabel = (row) => {
  const body = row.requestBody || {};
  return body.name || body.courseName || body.title || row.targetType || 'Record';
};
const getAuditSummary = (row) => {
  const summary = row.summary || '';
  if (summary && !isGenericUpdateSummary(summary)) return summary;
  const fields = getSubmittedFields(row.requestBody);
  if (fields.length) return `${getTargetLabel(row)} updated: ${fields.join(', ')}`;
  return summary || row.action || 'Updated record';
};
const getDevice = (row) => {
  const device = row.device || {};
  return [device.type, device.browser, device.os].filter(Boolean).join(' / ') || 'Unknown';
};

export default function AdminLogs() {
  const { user } = useAuth();
  const isInstitutionAdmin = user?.role === 'institution_admin';
  const [activeTab, setActiveTab] = useState('audit');
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    userId: '',
    role: '',
    from: todayStart(),
    to: '',
    method: '',
    statusCode: '',
    search: '',
  });

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const endpoint = useMemo(() => {
    if (activeTab === 'failed') return '/api/logs/logins';
    if (activeTab === 'devices') return '/api/logs/logins';
    if (activeTab === 'errors') return '/api/logs/errors';
    return `/api/logs/${activeTab}`;
  }, [activeTab]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 2000);
    return () => clearTimeout(handler);
  }, [filters.search]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, debouncedSearch, filters.userId, filters.role, filters.from, filters.to, filters.statusCode]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        userId: filters.userId || undefined,
        role: filters.role || undefined,
        from: filters.from || undefined,
        to: filters.to || undefined,
      };
      if (activeTab === 'audit') {
        params.action = debouncedSearch || undefined;
      }
      if (activeTab === 'errors') {
        params.statusCode = filters.statusCode || undefined;
        params.message = debouncedSearch || undefined;
      }
      if (activeTab === 'failed') params.success = false;
      if (activeTab === 'devices') params.contact = debouncedSearch || undefined;

      const res = await axios.get(endpoint, { params });
      setRows(res.data?.items || []);
      setTotal(res.data?.total || 0);
      setTotalPages(Math.ceil((res.data?.total || 0) / limit) || 1);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    axios.get('/api/logs/users')
      .then((res) => setUsers((res.data || []).filter((item) => ['admin', 'institution_admin'].includes(item.role))))
      .catch(() => setUsers([]));
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [activeTab, page, debouncedSearch, filters.userId, filters.role, filters.from, filters.to, filters.statusCode]);

  const updateFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const clearFilters = () => {
    setFilters({
      userId: '',
      role: '',
      from: '',
      to: '',
      method: '',
      statusCode: '',
      search: '',
    });
    setDebouncedSearch('');
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    
    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`relative inline-flex items-center px-4 py-2 text-sm font-semibold focus:z-20 ${
            page === i
              ? 'z-10 bg-indigo-600 text-white focus-visible:outline-focus-visible:outline-2 focus-visible:outline-indigo-600'
              : 'text-slate-900 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 focus:outline-offset-0'
          }`}
        >
          {i}
        </button>
      );
    }
    return pages;
  };

  const renderRows = () => {
    if (activeTab === 'audit') return rows.map((row) => (
      <tr key={row._id || row.id} className="border-b border-slate-100 align-top">
        <td className="px-4 py-3 text-sm font-semibold text-slate-900">{getActor(row)}</td>
        <td className="px-4 py-3 text-sm text-slate-600">{row.actorRole || '-'}</td>
        <td className="px-4 py-3 text-sm font-medium text-slate-800">{getAuditSummary(row)}</td>
        <td className="px-4 py-3 text-sm text-slate-600">{row.ipAddress || '-'}</td>
        <td className="px-4 py-3 text-sm text-slate-600">{getDevice(row)}</td>
        <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{formatDate(row.createdAt)}</td>
      </tr>
    ));

    if (activeTab === 'errors') return rows.map((row) => (
      <tr key={row._id || row.id} className="border-b border-slate-100 align-top">
        <td className="px-4 py-3 text-sm font-bold text-rose-600">{row.statusCode}</td>
        <td className="px-4 py-3 text-sm text-slate-600 max-w-md">{row.message}</td>
        <td className="px-4 py-3 text-sm text-slate-600">{row.ipAddress || '-'}</td>
        <td className="px-4 py-3 text-sm text-slate-600">{getDevice(row)}</td>
        <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{formatDate(row.createdAt)}</td>
      </tr>
    ));

    return rows.map((row) => (
      <tr key={row._id || row.id} className="border-b border-slate-100 align-top">
        <td className="px-4 py-3 text-sm font-semibold text-slate-900">{row.contact || row.userId || '-'}</td>
        <td className="px-4 py-3 text-sm text-slate-600">{row.role || '-'}</td>
        <td className="px-4 py-3 text-sm">
          <span className={`rounded px-2 py-1 text-xs font-bold ${row.success ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'}`}>
            {row.success ? 'Success' : 'Failed'}
          </span>
        </td>
        <td className="px-4 py-3 text-sm text-slate-600">{row.failureReason || '-'}</td>
        <td className="px-4 py-3 text-sm text-slate-600">{row.ipAddress || '-'}</td>
        <td className="px-4 py-3 text-sm text-slate-600">{getDevice(row)}</td>
        <td className="px-4 py-3 text-sm text-slate-500 whitespace-nowrap">{formatDate(row.createdAt)}</td>
      </tr>
    ));
  };

  const headers = activeTab === 'audit'
    ? ['User', 'Role', 'Change', 'IP', 'Device', 'Time']
    : activeTab === 'errors'
      ? ['Status', 'Error', 'IP', 'Device', 'Time']
      : ['Contact / User', 'Role', 'Result', 'Reason', 'IP', 'Device', 'Time'];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-black text-slate-900">Logs</h1>
          <p className="text-sm text-slate-500 mt-1">Audit changes, login history, errors, IP and device activity.</p>
        </div>
        <button onClick={fetchLogs} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-bold text-white">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold ${active ? 'bg-indigo-600 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}>
              <Icon size={16} /> {tab.label}
            </button>
          );
        })}
      </div>

      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-700"><Filter size={16} /> Filters</div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <select value={filters.userId} onChange={(e) => updateFilter('userId', e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="">All users</option>
            {users.map((user) => <option key={user.id || user._id} value={user.id || user._id}>{user.name || user.email || user.mobile}</option>)}
          </select>
          <select value={filters.role} onChange={(e) => updateFilter('role', e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm">
            <option value="">All roles</option>
            {!isInstitutionAdmin && <option value="admin">Admin</option>}
            <option value="institution_admin">Institution Admin</option>
            <option value="student">Student</option>
          </select>
          <input type="date" value={filters.from} onChange={(e) => updateFilter('from', e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          <input type="date" value={filters.to} onChange={(e) => updateFilter('to', e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          {activeTab === 'audit' ? (
            <div />
          ) : activeTab === 'errors' ? (
            <input placeholder="Status code" value={filters.statusCode} onChange={(e) => updateFilter('statusCode', e.target.value)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm" />
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-2.5 text-slate-400" />
              <input placeholder="Search" value={filters.search} onChange={(e) => updateFilter('search', e.target.value)} className="w-full rounded-lg border border-slate-200 pl-9 pr-3 py-2 text-sm" />
            </div>
            <button onClick={fetchLogs} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white">Apply</button>
            <button onClick={clearFilters} className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-600 hover:bg-slate-50">
              <XCircle size={15} /> Clear
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="text-sm font-bold text-slate-700">{total} records</p>
          {loading && <p className="text-xs text-slate-400">Loading...</p>}
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-slate-50">
              <tr>{headers.map((header) => <th key={header} className="px-4 py-3 text-left text-xs font-black uppercase tracking-wide text-slate-500">{header}</th>)}</tr>
            </thead>
            <tbody>
              {rows.length ? renderRows() : (
                <tr><td colSpan={headers.length} className="px-4 py-10 text-center text-sm text-slate-500">No logs found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 bg-white px-4 py-3 sm:px-6 mt-4 rounded-2xl shadow-sm">
          <div className="flex flex-1 justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className="relative inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page >= totalPages}
              className="relative ml-3 inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-700">
                Showing <span className="font-bold">{(page - 1) * limit + 1}</span> to{' '}
                <span className="font-bold">{Math.min(page * limit, total)}</span> of{' '}
                <span className="font-bold">{total}</span> results
              </p>
            </div>
            <div>
              <nav className="isolate inline-flex -space-x-px rounded-xl shadow-sm bg-white border border-slate-200 overflow-hidden" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="relative inline-flex items-center px-3 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50 font-bold border-r border-slate-200"
                >
                  &larr;
                </button>
                {renderPageNumbers()}
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                  className="relative inline-flex items-center px-3 py-2 text-slate-400 hover:bg-slate-50 disabled:opacity-50 font-bold border-l border-slate-200"
                >
                  &rarr;
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
